import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { IBeegoAppointment } from '@internal-models/beego-appointment.model';
import { IClient } from '@internal-models/client.model';
import { ILoan } from '@internal-models/loan.model';
import { IReservation } from '@internal-models/reservation.model';
import { IWaitListItem } from '@internal-models/wait-list-item.model';
import { BeegoAppointmentProvider } from '@internal-providers/beego-appointment.provider';
import { ClientProvider } from '@internal-providers/client.provider';
import { LoanProvider } from '@internal-providers/loan.provider';
import { ReservationProvider } from '@internal-providers/reservation.provider';
import { WaitListProvider } from '@internal-providers/wait-list.provider';
import { Action, ErrorType, UserPermissions } from '@shared-libs/enums';
import { translate } from '@shared-libs/helpers';
import { ErrorService } from '@shared-services/error.service';
import { NavigationService } from '@shared-services/navigation.service';
import { ValidationService } from '@shared-services/validation.service';
import { Subscription } from 'rxjs';

type MergeClient = IClient & {
	WaitList?: Array<IWaitListItem>;
	Reservations?: Array<IReservation>;
	BeegoAppointments?: Array<IBeegoAppointment>;
	Loans?: Array<ILoan>;
};

@Component({
	selector: 'merge-clients-desktop-page',
	templateUrl: 'merge-clients.desktop.page.html',
	styleUrls: ['./merge-clients.desktop.page.scss'],
})
export class MergeClientsDesktopPage implements OnDestroy, OnInit {
	public originClient: MergeClient;
	public targetClient: MergeClient;
	private routeSub: Subscription;

	public readonly UserPermissions: typeof UserPermissions = UserPermissions;

	constructor(
		private readonly router: Router,
		private readonly provider: ClientProvider,
		private readonly waitListProvider: WaitListProvider,
		private readonly reservationsProvider: ReservationProvider,
		private readonly beegoAppointmentProvider: BeegoAppointmentProvider,
		private readonly loanProvider: LoanProvider,
		private readonly validationService: ValidationService,
		private readonly navigationService: NavigationService,
		private readonly errorService: ErrorService
	) {}

	public ngOnInit(): void {
		this.routeSub = this.router.events.subscribe((event) => {
			if (event instanceof NavigationStart) {
				this.originClient = null;
				this.targetClient = null;
			}
		});
	}

	public ngOnDestroy(): void {
		this.routeSub.unsubscribe();
	}

	public async mergeClients(): Promise<void> {
		if (await this.validationService.actionIsConfirmed('MERGE_CLIENTS')) {
			await this.provider.mergeClients({
				originClientId: this.originClient.id,
				targetClientId: this.targetClient.id,
			});

			this.originClient = null;
			this.targetClient = null;
			this.navigationService.navigateBack({ action: Action.DELETE });
		}
	}

	public async onClientSelect(client: IClient, type: 'origin' | 'target'): Promise<void> {
		if (this.originClient?.id === client.id || this.targetClient?.id === client.id) {
			this.errorService.showApplicationError(translate('MERGE_CLIENTS.SAME_CLIENT'), ErrorType.PermanentToast);
			return;
		}

		if (type === 'origin') {
			this.originClient = client;

			const [Reservations, BeegoAppointments, WaitList, Loans] = await Promise.all([
				this.reservationsProvider.getAllOfClient(this.originClient.id),
				this.beegoAppointmentProvider.getAllOfClient(this.originClient.id),
				this.waitListProvider.getAllOfClient(this.originClient.id),
				this.loanProvider.getAllOfClient(this.originClient.id),
			]);

			this.originClient.Reservations = Reservations;
			this.originClient.BeegoAppointments = BeegoAppointments;
			this.originClient.WaitList = WaitList;
			this.originClient.Loans = Loans;
		}
		if (type === 'target') {
			this.targetClient = client;

			const [Reservations, BeegoAppointments, WaitList, Loans] = await Promise.all([
				this.reservationsProvider.getAllOfClient(this.targetClient.id),
				this.beegoAppointmentProvider.getAllOfClient(this.targetClient.id),
				this.waitListProvider.getAllOfClient(this.targetClient.id),
				this.loanProvider.getAllOfClient(this.targetClient.id),
			]);

			this.targetClient.Reservations = Reservations;
			this.targetClient.BeegoAppointments = BeegoAppointments;
			this.targetClient.WaitList = WaitList;
			this.targetClient.Loans = Loans;
		}
	}
}
